<template>
  <div class="checkbox" :class="classList">
    <div class="d-flex">
      <input
        type="checkbox"
        v-model="status"
        :name="name || id"
        :id="id"
        :value="value"
        @change="change"
        :tabindex="tabindex"
        :disabled="disabled && !isChecked"
      />
      <label :for="id">
      <span class="checkbox-visual" :class="{ '-checked': isChecked }">
        <SvgComponent :icon="icon" v-if="isChecked"></SvgComponent>
      </span>
        <span class="label">{{ label }}</span>
      </label>

      <SvgComponent
        v-if="loading"
        class="loadingSvg m-l-6"
        :icon="{
        name: 'Loading',
        color: Globals.colors.greenDark,
        size: small ? 12 : 18,
      }"
        key="loading"
      />
    </div>
    <div v-if="error">{{ errorMessage }}</div>
  </div>
</template>

<script>
import SvgComponent from '@/Scripts/components/shared/svg/Svg.vue';

export default {
  name: 'CheckBox',
  components: {
    SvgComponent
  },
  model: {
    prop: 'isChecked',
    event: 'change'
  },
  props: {
    /**
     * Gives the checkbox this ID.
     */
    id: {
      type: String,
      required: true
    },
    value: [String, Number],
    /**
     * True value renders small check box.
     */
    small: {
      type: Boolean,
      default: false
    },
    /**
     * True value renders medium check box | ***this one is not approved in design system.****
     */
    medium: {
      type: Boolean,
      default: false
    },
    /**
     * True value checks the check box by default.
     */
    isChecked: {
      type: Boolean,
      default: false
    },
    /**
     * The label to be displayed with the check box.
     */
    label: {
      type: String,
      default: ''
    },
    disabled: Boolean,
    tabindex: String,
    error: Boolean,
    errorMessage: String,
    loading: Boolean,
    name: String
  },
  watch: {
    isChecked: {
      immediate: true,
      handler (val) {
        this.status = val;
      }
    }
  },
  data () {
    return {
      status: false,
      icon: {
        name: 'Check',
        color: 'Black',
        size: 26
      }
    };
  },
  computed: {
    classList () {
      return {
        '-small': this.small,
        '-medium': this.medium,
        '-disabled': this.disabled,
        '-error': this.error
      };
    }
  },
  methods: {
    change (e) {
      this.$emit('change', e.target.checked);
    }
  }
};
</script>

<style lang="scss">
@import "@/scss/defaults.scss";

/**
* Standard Check Box
*/
.checkbox {
  input {
    opacity: 0;
    position: absolute;

    &:focus + label .checkbox-visual {
      .user-is-tabbing & {
        box-shadow: $focusTabbing;
      }
    }
  }

  label {
    display: flex;
    align-items: flex-start;
    cursor: pointer;
    margin-bottom: 0;

    .label {
      // word-break: break-word;

      .apple-device & {
        margin-top: 1px;
      }
    }

    .checkbox-visual {
      border: 1.5px solid $gray-dark;
      width: 24px;
      height: 24px;
      border-radius: 5px;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      margin-right: 9px;
      flex-shrink: 0;
    }
  }
}

/**
* Small Check Box
*/
.checkbox.-small label {
  .checkbox-visual {
    width: 15px;
    height: 15px;
    border: 1.5px solid $gray-dark;
    border-radius: 3px;
    margin-right: 6px;
  }

  .label {
    margin-top: -2px;

    .apple-device & {
      margin-top: 0px;
    }
  }
}

/**
* medium Check Box
*/
.checkbox.-medium label {
  .checkbox-visual {
    width: 18px;
    height: 18px;
    border: 1px solid $gray-dark;
    border-radius: 3px;
    margin-right: 9px;
  }

  .label {
    margin-top: -2px;
  }
}

/**
* disabled
*/
.checkbox.-disabled {
  pointer-events: none;

  label,
  input,
  .checkbox-visual {
    cursor: default !important;
  }

  label {
    .checkbox-visual {
      border-color: $gray;

      svg g {
        fill: $gray;
      }
    }

    .label {
      color: $gray;
    }
  }
}

.checkbox.-error {
  .checkbox-visual {
    border: 1px solid $red;
  }
}
</style>
